import React, { useState, useRef, useEffect } from "react";
import { Form } from "react-bootstrap";
import { ErrorMessage } from "formik";
import { BsCalendar3 } from "react-icons/bs";
import { RotatingLines } from "react-loader-spinner";

const DatePickerField = ({
  className,
  name,
  placeholder = "MM/DD/YYYY",
  value,
  onChange,
  onBlur,
  asterick,
  errorMessage,
  label,
  disabled = false,
  minDate = null,
  maxDate = null,
  onAutoSubmit = null,
  isSubmitting = false,
  submitSuccess = false,
  submitFail = false,
  managedOpen = null,
  hideInput = false,
}) => {
  const [isOpen, setIsOpen] = useState(managedOpen !== null ? managedOpen : false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [displayValue, setDisplayValue] = useState("");
  const pickerRef = useRef(null);

  useEffect(() => {
    if (value) {
      const date = new Date(value);
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const year = date.getFullYear();
      setDisplayValue(`${month}/${day}/${year}`);
      setCurrentMonth(date);
    } else {
      setDisplayValue("");
    }
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    if (managedOpen !== null) {
      setIsOpen(managedOpen);
    }
  }, [managedOpen]);

  const daysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const firstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const handlePrevMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1)
    );
  };

  const handleNextMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1)
    );
  };

  const handleDateClick = async (day) => {
    const selectedDate = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      day
    );

    if (minDate && selectedDate < minDate) return;
    if (maxDate && selectedDate > maxDate) return;

    const year = selectedDate.getFullYear();
    const month = String(selectedDate.getMonth() + 1).padStart(2, "0");
    const dayStr = String(selectedDate.getDate()).padStart(2, "0");
    const formattedDate = `${year}-${month}-${dayStr}`;

    if (onAutoSubmit) {
      await onAutoSubmit(formattedDate);
    } else {
      onChange({ target: { name, value: formattedDate } });
    }

    setIsOpen(false);
  };

  const handleClear = () => {
    if (onAutoSubmit) {
      return;
    }
    onChange({ target: { name, value: "" } });
    setIsOpen(false);
  };

  const handleToday = async () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    const formattedDate = `${year}-${month}-${day}`;

    if (onAutoSubmit) {
      await onAutoSubmit(formattedDate);
    } else {
      onChange({ target: { name, value: formattedDate } });
    }

    setCurrentMonth(today);
    setIsOpen(false);
  };

  const isDateDisabled = (day) => {
    const date = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      day
    );

    date.setHours(0, 0, 0, 0);

    if (minDate) {
      const minDateCopy = new Date(minDate);
      minDateCopy.setHours(0, 0, 0, 0);
      if (date < minDateCopy) return true;
    }

    if (maxDate) {
      const maxDateCopy = new Date(maxDate);
      maxDateCopy.setHours(0, 0, 0, 0);
      if (date > maxDateCopy) return true;
    }

    return false;
  };

  const isDateSelected = (day) => {
    if (!value) return false;
    const selectedDate = new Date(value);
    return (
      selectedDate.getDate() === day &&
      selectedDate.getMonth() === currentMonth.getMonth() &&
      selectedDate.getFullYear() === currentMonth.getFullYear()
    );
  };

  const renderCalendar = () => {
    const days = [];
    const totalDays = daysInMonth(currentMonth);
    const firstDay = firstDayOfMonth(currentMonth);

    const prevMonthDays = daysInMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1)
    );

    for (let i = firstDay - 1; i >= 0; i--) {
      days.push(
        <div key={`prev-${i}`} className="calendar-day disabled">
          {prevMonthDays - i}
        </div>
      );
    }

    for (let day = 1; day <= totalDays; day++) {
      const disabled = isDateDisabled(day);
      const selected = isDateSelected(day);

      days.push(
        <div
          key={day}
          className={`calendar-day ${disabled ? "disabled" : ""} ${
            selected ? "selected" : ""
          }`}
          onClick={() => !disabled && handleDateClick(day)}
        >
          {day}
        </div>
      );
    }

    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      days.push(
        <div key={`next-${i}`} className="calendar-day disabled">
          {i}
        </div>
      );
    }

    return days;
  };

  const getBorderStyle = () => {
    if (submitSuccess) {
      return {
        borderColor: "green",
        boxShadow: "0 0 5px green",
      };
    }
    if (submitFail) {
      return {
        borderColor: "red",
        boxShadow: "0 0 5px red",
      };
    }
    return {};
  };

  return (
    <Form.Group className={className}>
      <Form.Label htmlFor={name}>
        {label}
        {asterick && <span className="text-danger">*</span>}
      </Form.Label>
      <div className="custom-datepicker-wrapper" ref={pickerRef}>
      {!hideInput && (
        <div className="custom-datepicker-input-group">
          <Form.Control
            id={name}
            type="text"
            name={name}
            placeholder={placeholder}
            value={displayValue}
            onChange={(e) => setDisplayValue(e.target.value)}
            onFocus={() => !disabled && setIsOpen(true)}
            onBlur={onBlur}
            disabled={disabled}
            autoComplete="off"
            readOnly
            style={{
              border: "1px solid #dee2e6",
              borderRadius: "4px",
              ...(disabled
                ? {
                    backgroundColor: "#fff",
                    cursor: "not-allowed",
                    color: "#8A92A6",
                  }
                : {}),
              ...getBorderStyle(),
            }}
          />
          <div
            className="calendar-icon"
            onClick={() => !disabled && setIsOpen(!isOpen)}
          >
            {isSubmitting ? (
              <RotatingLines
                strokeWidth="5"
                animationDuration="0.75"
                width="20"
                strokeColor="#b2b4b9"
                visible={true}
              />
            ) : (
              <BsCalendar3 />
            )}
          </div>
        </div>
      )}

        {isOpen && (
          <div className="custom-datepicker-dropdown">
            <div className="calendar-header">
              <button
                type="button"
                className="calendar-nav-btn"
                onClick={handlePrevMonth}
              >
                ←
              </button>
              <div className="calendar-month-year">
                {monthNames[currentMonth.getMonth()]}{" "}
                {currentMonth.getFullYear()}
              </div>
              <button
                type="button"
                className="calendar-nav-btn"
                onClick={handleNextMonth}
              >
                →
              </button>
            </div>

            <div className="calendar-weekdays">
              <div className="calendar-weekday">Su</div>
              <div className="calendar-weekday">Mo</div>
              <div className="calendar-weekday">Tu</div>
              <div className="calendar-weekday">We</div>
              <div className="calendar-weekday">Th</div>
              <div className="calendar-weekday">Fr</div>
              <div className="calendar-weekday">Sa</div>
            </div>

            <div className="calendar-days">{renderCalendar()}</div>

            <div className="calendar-footer">
              <button
                type="button"
                className="calendar-footer-btn clear-btn"
                onClick={handleClear}
              >
                Clear
              </button>
              <button
                type="button"
                className="calendar-footer-btn today-btn"
                onClick={handleToday}
              >
                Today
              </button>
            </div>
          </div>
        )}
      </div>

      <ErrorMessage name={name} component="div" className="text-danger" />
      {errorMessage &&
        errorMessage.length > 0 &&
        errorMessage.map((error, index) => (
          <span key={index} className="w-100 mt-1 small text-danger">
            {error}
            <br />
          </span>
        ))}
    </Form.Group>
  );
};

export default DatePickerField;
